import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Eye, EyeOff } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useSetPassword } from "@/features/auth/api/useSetPassword";
import { supabase } from "@/services/supabase";
import { AuthLayout, AuthLegalFooter } from "@/features/auth/components/AuthLayout";

const setPasswordSchema = z
  .object({
    password: z.string().min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type SetPasswordFormValues = z.infer<typeof setPasswordSchema>;

interface SetPasswordFormProps extends React.ComponentProps<"div"> {
  onSubmit?: (e: React.FormEvent) => void;
}

const authPrimaryBtn =
  "w-full motion-safe:transition-transform motion-safe:duration-200 motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-panel";

export function SetPasswordForm({
  className,
  onSubmit: _onSubmit,
  ...props
}: SetPasswordFormProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");
  const redirectTo = searchParams.get("redirect_to");
  const isReset = location.pathname.includes("reset-password");

  useEffect(() => {
    if (redirectTo && !token) {
      console.log("User redirected after token verification, completing setup");
    }
  }, [redirectTo, token]);

  const {
    setPassword: submitPassword,
    loading,
    error: apiError,
  } = useSetPassword({
    onSuccess: () => {
      navigate("/", { replace: true });
    },
  });

  const form = useForm<SetPasswordFormValues>({
    resolver: zodResolver(setPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const handleSubmit = async (values: SetPasswordFormValues) => {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session && !token && !redirectTo) {
      form.setError("root", {
        message: "Invalid or missing invitation information",
      });
      return;
    }

    await submitPassword({
      password: values.password,
      token: token || undefined,
    });
  };

  const error =
    form.formState.errors.root?.message || apiError?.message || null;

  return (
    <div className={cn(className)} {...props}>
      <AuthLayout
        title="Set your password"
        description={
          isReset
            ? "Choose a password to continue."
            : "Create a password to complete your account setup."
        }
        footer={<AuthLegalFooter />}
      >
        <Form {...form}>
          <form
            className="flex flex-col gap-5"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            {error && (
              <div className="rounded-sm bg-destructive px-3 py-2.5 text-sm text-destructive-foreground">
                {error}
              </div>
            )}
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        autoComplete="new-password"
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-slate-current" />
                        ) : (
                          <Eye className="h-4 w-4 text-slate-current" />
                        )}
                        <span className="sr-only">
                          {showPassword ? "Hide password" : "Show password"}
                        </span>
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="••••••••"
                        autoComplete="new-password"
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4 text-slate-current" />
                        ) : (
                          <Eye className="h-4 w-4 text-slate-current" />
                        )}
                        <span className="sr-only">
                          {showConfirmPassword
                            ? "Hide password"
                            : "Show password"}
                        </span>
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className={authPrimaryBtn}
              disabled={loading}
            >
              {loading ? "Saving…" : "Set password"}
            </Button>
          </form>
        </Form>
      </AuthLayout>
    </div>
  );
}
